// ════════════════════════════════════════════════════════════════════════
//  Vom Hinweis zur Stelle
//
//  Jeder Hinweis trägt einen `path` – aber der war bisher nur Text vor dem
//  Doppelpunkt. Der häufigste Redaktionsfehler entsteht in einer Lektion
//  („Zeichen x hat keine Finger-Zuordnung“) und wird in den Grundlagen behoben:
//  der weite Weg lässt sich nicht abschaffen, aber man muss ihn nicht selbst
//  suchen müssen.
//
//  Warum das Bundle als Argument: Lektions- und Stufenschlüssel dürfen Punkte
//  enthalten (`grundreihe-4a.stufe-1`). Ein Pfad wie
//  `lessons.grundreihe-4a.grundreihe-4a.stufe-1.text` ist am Punkt nicht zu
//  zerlegen – wohl aber am längsten Schlüssel, den es wirklich gibt.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle, Problem } from "@otto/lehrgang";
import { coursePath } from "../../../shared/lib/coursePath.js";

export type ProblemTarget = {
  /** Adresse samt Anker. */
  readonly to: string;
  /** Wohin es geht, in einem Wort – steht als Linktext neben dem Hinweis. */
  readonly label: string;
};

const keyboard = (slug: string) => coursePath(slug, "grundlagen/tastatur");
const basics = (slug: string) => coursePath(slug, "grundlagen/sterne");
const assessment = (slug: string) => coursePath(slug, "grundlagen/test");

/** Anker für ein Feld – dieselbe Bildung auf beiden Seiten, Link wie Ziel. */
export const anchors = {
  stars: "sterne",
  fingers: "finger",
  keyboardRows: "tastaturbild",
  homeRow: "grundstellung",
  lessonKeys: "tasten",
  lessonUppercase: "grossbuchstaben",
  assessment: "test",
  stage: (stageKey: string) => `stufe-${stageKey}`,
} as const;

/**
 * Das Ziel eines Hinweises.
 *
 * Unbekannte Pfade landen auf dem Überblick. Ein neuer Prüfpfad in
 * `packages/lehrgang` darf hier keinen toten Link erzeugen – dass er auf der
 * falschen Seite landet, merkt man; dass ein Link nirgends hinführt, nicht.
 */
export function problemTarget(
  problem: Problem,
  bundle: Bundle | null,
  slug: string,
  published: Bundle | null = null,
): ProblemTarget {
  const { path } = problem;
  const KEYBOARD = keyboard(slug);
  const BASICS = basics(slug);

  // Der wichtigste Fall zuerst, und er hört auf die MELDUNG statt auf den Pfad:
  // „Zeichen q hat keine Finger-Zuordnung“ erscheint an der Stufe, weil dort das
  // Zeichen steht – zu tun ist aber etwas in der Fingertabelle. Der Pfad sagt,
  // wo ein Hinweis auffällt; nur die Meldung weiß, wo er behoben wird.
  //
  // Die Formulierung stammt aus checkBundle in packages/lehrgang. Ändert sie
  // sich, fällt das im Test „führt vom Zeichen ohne Finger zur Fingertabelle“
  // auf – der prüft gegen die echte Ausgabe der Prüfung, nicht gegen einen
  // abgeschriebenen Satz.
  if (problem.message.includes("keine Finger-Zuordnung")) {
    return { to: `${KEYBOARD}#${anchors.fingers}`, label: "Tastatur und Finger" };
  }

  if (path === "starThresholds")
    return { to: `${BASICS}#${anchors.stars}`, label: "Sterne" };
  if (path === "assessment" || path.startsWith("assessment.")) {
    return { to: `${assessment(slug)}#${anchors.assessment}`, label: "Test" };
  }
  if (/^fingers\.\d+$/.test(path)) {
    return { to: `${KEYBOARD}#${anchors.fingers}`, label: "Tastatur und Finger" };
  }
  if (/^keyboard\.rows\.\d+$/.test(path)) {
    return { to: `${KEYBOARD}#${anchors.keyboardRows}`, label: "Tastaturbild" };
  }
  if (path === "keyboard.homeRow") {
    return { to: `${KEYBOARD}#${anchors.homeRow}`, label: "Grundstellung" };
  }

  // Der ganze Lehrgang hat noch keine Lektion. Der Weg führt in die Liste, wo
  // „Neue Lektion“ steht – nicht auf den Überblick, wo nichts zu tun ist.
  if (path === "lessons") {
    return { to: coursePath(slug, "lektionen"), label: "Lektionen" };
  }

  if (bundle && path.startsWith("lessons.")) {
    const rest = path.slice("lessons.".length);
    // Auch gegen die veröffentlichte Fassung: ein Hinweis kann von einer
    // Lektion handeln, die im Entwurf ENTFERNT wurde und an der inzwischen
    // geübt wird. Ohne sie fiele der Weg auf den Überblick zurück – und ein
    // Link, der nirgends hinführt, fällt niemandem auf.
    const lesson = longestMatch(
      [
        ...bundle.lessons.map((l) => l.key),
        ...(published?.lessons.map((l) => l.key) ?? []),
      ],
      rest,
    );
    if (lesson) {
      const at = coursePath(slug, `lektionen/${lesson}`);
      const tail = rest.slice(lesson.length + 1);
      if (tail === "") return { to: at, label: "Lektion" };
      if (tail === "keys")
        return { to: `${at}#${anchors.lessonKeys}`, label: "Tasten der Lektion" };
      if (tail === "uppercaseWith") {
        return { to: `${at}#${anchors.lessonUppercase}`, label: "Großbuchstaben" };
      }
      const stages = [
        ...(bundle.lessons.find((l) => l.key === lesson)?.stages ?? []),
        ...(published?.lessons.find((l) => l.key === lesson)?.stages ?? []),
      ];
      const stage = longestMatch(
        stages.map((s) => s.key),
        tail,
      );
      if (stage) return { to: `${at}#${anchors.stage(stage)}`, label: "Stufe" };
      return { to: at, label: "Lektion" };
    }
  }

  return { to: coursePath(slug, "uebersicht"), label: "Überblick" };
}

/**
 * Der längste Schlüssel, der am Anfang von `rest` steht.
 *
 * Der längste, nicht der erste: gäbe es `grundreihe-4` und `grundreihe-4a`,
 * träfe der erste Fund die falsche Lektion.
 */
function longestMatch(keys: readonly string[], rest: string): string | null {
  let best: string | null = null;
  for (const key of keys) {
    if (rest !== key && !rest.startsWith(`${key}.`)) continue;
    if (best === null || key.length > best.length) best = key;
  }
  return best;
}
